import { useEffect, useRef, useState } from "react";
import StarOutlineIcon from "../../../../assets/images/star_outline_icon.svg?react";
import StarFilledIcon from "../../../../assets/images/star_filled_icon.svg?react";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { useToggleResourceFavorite } from "../../domain/hooks/use_toggle_resource_favorite";
import { useGetMyUserProfile } from "../../../../core/components/user/domain/hooks/use_get_user_profile";
import { useTranslation } from "../../../../i18n/useTranslation";

interface Props {
  resourceId: string;
  initialFavoriteId: string | null;
  className?: string;
  outlineColor?: string;
}

const ResourceFavoriteButton: React.FC<Props> = ({
  resourceId,
  initialFavoriteId,
  className = "",
  outlineColor,
}) => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const { data: user } = useGetMyUserProfile();
  const userId = user?.userId;

  const [isFav, setIsFav] = useState(!!initialFavoriteId);
  const [favoriteId, setFavoriteId] = useState<string | null>(initialFavoriteId);
  const [bounce, setBounce] = useState(false);
  const didMountRef = useRef(false);
  const { mutate, isPending } = useToggleResourceFavorite();

  useEffect(() => {
    setIsFav(!!initialFavoriteId);
    setFavoriteId(initialFavoriteId);
  }, [initialFavoriteId, resourceId]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setBounce(true);
    const id = window.setTimeout(() => setBounce(false), 300);
    return () => window.clearTimeout(id);
  }, [isFav]);

  const toggle = () => {
    if (!userId || isPending) return;
    const next = !isFav;
    const prevFavoriteId = favoriteId;
    setIsFav(next);
    if (!next) setFavoriteId(null);
    mutate(
      { resourceId, userId, _favoriteId: prevFavoriteId, next },
      {
        onSuccess: (data) => {
          setFavoriteId(data._favoriteId);
        },
        onError: () => {
          setIsFav(!next);
          setFavoriteId(prevFavoriteId);
        },
      }
    );
  };

  const color = isFav ? theme.UvibesPink : (outlineColor ?? theme.UvibesPink);

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!userId}
      aria-label={isFav ? t('course.resource.favorite_remove') : t('course.resource.favorite_add')}
      aria-pressed={isFav}
      className={`cursor-pointer w-6 h-6 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{
        color,
        transform: bounce ? "scale(1.25)" : "scale(1)",
        transition: "transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    >
      <span className="relative inline-block w-5 h-5">
        <StarOutlineIcon className="absolute inset-0 w-5 h-5" />
        <StarFilledIcon
          className="absolute inset-0 w-5 h-5"
          style={{
            opacity: isFav ? 1 : 0,
            transform: isFav ? "scale(1)" : "scale(0.4)",
            transformOrigin: "center",
            transition: "opacity 220ms ease-out, transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        />
      </span>
    </button>
  );
};

export default ResourceFavoriteButton;
